/**
 * GlobalBillingBanners
 *
 * Mounted once in App.tsx. Fetches subscription status and renders
 * whichever global billing banner is relevant:
 *
 *   A8 SubscriptionHealthBanner — halted / pending / cancelled_scheduled
 *   A6 TrialChargeReminderBanner — trial converting to paid within TRIAL_WARN_DAYS
 *
 * Returns null when unauthenticated (API 401), no subscription, or no
 * actionable state. Deliberately independent from FinanceBilling so the
 * banner appears on every page, not just Settings.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSidebar } from "../../contexts/SidebarContext";
import { BillingAPI, SubscriptionStatus } from "../../services/billing.service";
import { SubscriptionHealthBanner, HealthBannerVariant } from "./SubscriptionHealthBanner";
import { TrialChargeReminderBanner } from "./TrialChargeReminderBanner";
import {
    PLAN_BASE_ANNUAL_PRICE_INR,
} from "../../constants";
import { daysLeft } from "../../utils/billing";

const TRIAL_WARN_DAYS = 7;

function getChargeDisplay(planName: string | null): string {
    if (!planName) return `₹${PLAN_BASE_ANNUAL_PRICE_INR.toLocaleString("en-IN")}`;
    // Pilot sellable plan is Base annual @ ₹14,499. Legacy names still map here.
    if (planName.toLowerCase().includes("annual") || planName.toLowerCase().includes("base")) {
        return `₹${PLAN_BASE_ANNUAL_PRICE_INR.toLocaleString("en-IN")}`;
    }
    return `₹${PLAN_BASE_ANNUAL_PRICE_INR.toLocaleString("en-IN")}`;
}

function deriveHealthVariant(status: string | null): HealthBannerVariant | null {
    if (status === "halted") return "halted";
    if (status === "pending") return "pending";
    if (status === "cancelled") return "cancelled_scheduled";
    return null;
}

function getPlanLabel(planName: string | null): string {
    if (!planName) return "subscription";
    if (planName.toLowerCase().includes("premium")) return "Kaagazy+";
    if (planName.toLowerCase().includes("base")) return "Kaagazy";
    return planName;
}

export const GlobalBillingBanners: React.FC = () => {
    const [sub, setSub] = useState<SubscriptionStatus | null>(null);
    const navigate = useNavigate();
    const { isExpanded } = useSidebar();
    const sidebarOffset = isExpanded ? 240 : 72;
    const goToBilling = () => navigate("/settings", { state: { section: "finance" } });

    useEffect(() => {
        BillingAPI.getSubscription()
            .then(setSub)
            .catch(() => {}); // unauthenticated or no subscription — render nothing
    }, []);

    const wrap = (content: React.ReactNode) => (
        <div className="w-full" style={{ paddingLeft: sidebarOffset }}>
            {content}
        </div>
    );

    if (!sub || !sub.has_subscription) return null;

    const healthVariant = deriveHealthVariant(sub.status);
    const planLabel = getPlanLabel(sub.plan_name);

    const trialFlag = sub.feature_flags.find((f) => f.is_trial && f.expires_at);
    const trialDays = trialFlag?.expires_at ? daysLeft(trialFlag.expires_at) : 0;
    const showTrialCharge = !healthVariant && trialDays > 0 && trialDays <= TRIAL_WARN_DAYS;

    return wrap(
        <>
            {healthVariant && (
                <SubscriptionHealthBanner
                    variant={healthVariant}
                    planLabel={planLabel}
                    periodEndDate={sub.current_period_end}
                    onAction={goToBilling}
                />
            )}
            {showTrialCharge && trialFlag?.expires_at && (
                <TrialChargeReminderBanner
                    trialEndsAt={trialFlag.expires_at}
                    chargeAmount={getChargeDisplay(sub.plan_name)}
                    onManage={goToBilling}
                />
            )}
        </>
    );
};

export default GlobalBillingBanners;
